import { useState, useEffect, useRef } from "react";
import { API, authFetch } from "@/App";
import { Bell, Check, CheckCheck, MessageSquare, X, Loader2 } from "lucide-react";

export function NotificationBell({ onNavigateToOrder, orders = [], refreshTrigger = 0 }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Resolve order name & line number from current orders data
  const resolveOrderInfo = (notification) => {
    const orderIdx = orders.findIndex(o => o.order_id === notification.order_id);
    const order = orderIdx >= 0 ? orders[orderIdx] : null;
    const clientName = order?.extracted_data?.NAME || order?.extracted_data?.client_name || notification.order_name || "";
    const lineNum = orderIdx >= 0 ? orderIdx + 1 : null;
    return { clientName, lineNum };
  };

  useEffect(() => {
    loadNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Refresh when trigger changes (e.g. when a comment is marked as read)
  useEffect(() => {
    if (refreshTrigger > 0) loadNotifications();
  }, [refreshTrigger]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await authFetch(`${API}/notifications`, {
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await authFetch(`${API}/notifications/${notificationId}/read`, {
        method: "PUT",
      });
      
      setNotifications(prev => 
        prev.map(n => 
          n.notification_id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    setIsLoading(true);
    try {
      await authFetch(`${API}/notifications/read-all`, {
        method: "PUT",
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    
    if (onNavigateToOrder) {
      onNavigateToOrder(notification.order_id);
    }
    
    setIsOpen(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit"
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-zinc-100 rounded-lg transition-colors"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-orange-500' : 'text-zinc-600'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
            <h3 className="font-semibold text-zinc-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={isLoading}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {isLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCheck className="w-3 h-3" />
                )}
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.filter(n => !n.read).length === 0 ? (
              <div className="px-4 py-8 text-center text-zinc-400">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.filter(n => !n.read).map((notification) => {
                const { clientName, lineNum } = resolveOrderInfo(notification);
                return (
                <button
                  key={notification.notification_id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full px-4 py-3 text-left border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${
                    !notification.read ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-full ${
                      !notification.read ? 'bg-blue-100' : 'bg-zinc-100'
                    }`}>
                      <MessageSquare className={`w-4 h-4 ${
                        !notification.read ? 'text-blue-600' : 'text-zinc-500'
                      }`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-zinc-900 truncate">
                          {notification.from_user_name}
                        </span>
                        {!notification.read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-zinc-600 mt-0.5">
                        vous a mentionné — {lineNum ? `Ligne ${lineNum}` : ''}{lineNum && clientName ? ' · ' : ''}<span className="font-semibold text-zinc-900">{clientName || notification.order_name || "une commande"}</span>
                      </p>
                      <p className="text-xs text-zinc-400 mt-1 truncate italic">
                        "{notification.preview}"
                      </p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
